"use client";

import Link from "next/link";
import { useState, type FormEvent } from "react";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { useRequestPasswordReset } from "@/hooks/useAuth";

export default function ForgotPasswordPage() {
  const reset = useRequestPasswordReset();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const errData = (reset.error as Error & { data?: Record<string, unknown> })
    ?.data;
  const emailError =
    errData && "email" in errData
      ? Array.isArray(errData.email)
        ? String(errData.email[0])
        : String(errData.email)
      : undefined;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await reset.mutateAsync(email);
      setSubmitted(true);
    } catch {
      // error in reset.error
    }
  }

  if (submitted) {
    return (
      <AuthLayout title="Check your email">
        <div className="text-center">
          <p className="text-ink-600 text-sm">
            If an account with that email exists, a password reset link has been
            sent to <span className="text-ink-900 font-medium">{email}</span>.
          </p>
          <Link
            href="/login"
            className="text-gold-600 hover:text-gold-700 mt-6 inline-block text-sm font-medium"
          >
            Back to sign in
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Forgot password"
      subtitle="Enter your email to receive a reset link"
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {(reset.error as Error)?.message && !emailError && (
          <div
            role="alert"
            className="border-danger bg-danger/5 text-danger rounded-md border px-3 py-2 text-sm"
          >
            {(reset.error as Error)?.message}
          </div>
        )}
        <div>
          <label
            htmlFor="email"
            className="text-ink-700 mb-1.5 block text-sm font-medium"
          >
            Email
          </label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            hasError={!!emailError}
            placeholder="you@example.com"
            autoComplete="email"
          />
          {emailError && (
            <p className="text-danger mt-1 text-sm">{emailError}</p>
          )}
        </div>
        <Button
          type="submit"
          className="w-full"
          size="lg"
          isLoading={reset.isPending}
        >
          Send reset link
        </Button>
      </form>
      <p className="text-ink-500 mt-6 text-center text-sm">
        <Link
          href="/login"
          className="text-gold-600 hover:text-gold-700 font-medium"
        >
          Back to sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
